import React, { useMemo } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, IconButton, Stack, Tooltip, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { buildAssetUrl, formatDateTime, truncateText } from '../utils';

export default function VersionsDrawer({ scene, review, onClose }) {
  const open = Boolean(scene && review);
  const selectedId = Number(scene?.selectedVersionId) || 0;

  const sortedVersions = useMemo(() => {
    const arr = Array.isArray(scene?.versions) ? [...scene.versions] : [];
    return arr.sort((a, b) => Number(b.versionNumber) - Number(a.versionNumber));
  }, [scene?.versions]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>
        Histórico de Versões — Cena #{String(scene?.orderIndex ?? scene?.id).padStart(2, '0')}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!sortedVersions.length ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Nenhuma versão registrada.
          </Typography>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {sortedVersions.map((v) => {
              const directUrl = v?.imageUrl || v?.derivedImageUrl || v?.imageAsset?.url || v?.imageAsset?.publicUrl || v?.imageAsset?.storageUrl || '';
              const assetUrl = directUrl || (
                review?.getAssetUrl
                  ? review.getAssetUrl(v?.imageAsset || { id: v.imageAssetId }, v.imageAssetId)
                  : buildAssetUrl({ id: v.imageAssetId, storageUrl: null })
              );
              const videoUrl = v?.videoUrl || v?.derivedVideoUrl || v?.videoAsset?.url || v?.videoAsset?.publicUrl || v?.videoAsset?.storageUrl || '';
              function isVideoAsset(asset) {
                const kind = String(asset?.kind || '').toLowerCase();
                const mime = String(asset?.mimeType || asset?.mime || '').toLowerCase();
                if (kind === 'video') return true;
                if (mime.startsWith('video/')) return true;
                const name = String(asset?.originalName || asset?.fileName || asset?.name || '').toLowerCase();
                return /\.(mp4|webm|mov|m4v|mkv|avi|3gp|ogv)$/.test(name);
              }
              const metadataKind = String((() => {
                try { return JSON.parse(v?.metadataJson || 'null')?.kind || ''; } catch (_) { return ''; }
              })() || '').toLowerCase();
              const isVideo = metadataKind === 'video'
                || (isVideoAsset(v?.videoAsset) && !!videoUrl)
                || (assetUrl && isVideoAsset(v?.imageAsset));
              const previewUrl = isVideo ? (videoUrl || assetUrl) : assetUrl;
              const isSelected = selectedId === Number(v.id);
              const busy = review?.isSceneBusy?.(scene?.id);
              return (
                <Grid item xs={12} sm={6} md={4} key={String(v.id)}>
                  <Box
                    sx={{
                      border: isSelected ? '2px solid' : '1px solid',
                      borderColor: isSelected ? 'success.main' : 'divider',
                      borderRadius: 2, p: 1.5,
                      bgcolor: isSelected ? 'success.light' : 'transparent',
                      display: 'flex', flexDirection: 'column', gap: 1, height: '100%'
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} useFlexGap>
                      <Stack direction="row" alignItems="center" spacing={1} useFlexGap>
                        <Chip size="small" label={`v${String(v.versionNumber)}`} color={isSelected ? 'success' : 'default'} />
                        <Chip size="small" label={String(v.origin || 'generated')} variant="outlined" />
                        {isSelected && (
                          <Tooltip title="Versão ativa">
                            <Chip size="small" icon={<CheckCircleIcon fontSize="x-small" />} label="Ativa" color="success" variant="filled" />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">{formatDateTime(v.createdAt)}</Typography>
                    </Stack>
                    <Box
                      sx={{
                        aspectRatio: '16 / 9', width: '100%',
                        bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                      onClick={() => !isVideo && previewUrl && review?.setPreviewImage?.(previewUrl)}
                      style={{ cursor: previewUrl && !isVideo ? 'zoom-in' : 'default' }}
                    >
                      {previewUrl
                        ? (
                          isVideo
                            ? <Box component="video" src={previewUrl} controls preload="metadata" playsInline sx={{ width: '100%', height: '100%', objectFit: 'cover', bgcolor: '#000' }} />
                            : <Box component="img" src={previewUrl} alt={`v${String(v.versionNumber)}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                        )
                        : <Typography color="text.disabled" variant="caption">Sem asset</Typography>}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Prompt</Typography>
                      <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', display: 'block' }}>
                        {truncateText(v.prompt, 220) || '-'}
                      </Typography>
                    </Box>
                    <Divider />
                    <Button
                      size="small"
                      fullWidth
                      variant={isSelected ? 'soft' : 'contained'}
                      color="success"
                      disabled={isSelected || busy}
                      startIcon={<CheckCircleIcon fontSize="small" />}
                      onClick={() => review?.selectVersion?.(scene.id, v.id)}
                    >{isSelected ? 'Selecionada' : 'Selecionar esta versão'}</Button>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
